import * as React from "react";
import { useEffect, useState, Profiler } from "react";
import { GetServerSideProps } from "next";
import { UAParser } from "ua-parser-js";
import dynamic from "next/dynamic";

// Components
import { Branding } from "@/components/OfferPage/Branding";
import { Header } from "@/components/OfferPage/Header";
import { OfferSEOHeader } from "@/components/OfferPage/OfferSEOHeader";
import { OfferList } from "@/components/OfferPage/OfferList";
import { OfferClaimRibbon } from "@/components/OfferPage/OfferClaimRibbon";
import { OfferClaimPopup } from "@/components/OfferPage/OfferClaimPopup";
import { LowerContentBlock } from "@/components/common/LowerContentBlock";
import AuthorSection from "@/components/common/AuthorSection";

//Styles
import commonStyles from "@/styles/Common.module.css";
import styles from "@/pages/offers/Offers.module.css";

// API
import { getOfferPage } from "src/api/offers";
import { getGlobalConfig } from "src/api/globalConfig";
import { visitReport } from "src/api/report/visit";
import { isLandingPageCompleted } from "src/api/report/index";
import { getSitePopulateQuery } from "../api/sites";

// Types
import type { OfferPageTemplateResponse } from "src/types/offers";
import type { Site, USPAttributesGroup } from "src/types/sites";
import type { PageData, PageProps, SessionCookie, UserCookie } from "@/types/nextjs";

// Utils
import { makeOfferPageTheme } from "src/utils/theme";
import {
    getPageUSPAttributes,
    logNewGoogleAnalyticsEvent,
} from "src/utils/utils";
import {
    getAppProps,
    getPageThemeAndUpdateCookies,
} from "../utils/globalServerData";
import { getPageAndSite } from "src/utils/getPageAndSite";
import {
    getDefaultOfferPageFromSite,
    getOfferClaimCounts,
} from "../utils/serverOfferPageSetupUtils";
import { validateOfferPage } from "../utils/clientOfferPageSetupUtils";
import { submitFromSocials } from "@/components/landing/DynamicForm/formSubmitUtils";
import { sanitiseGeoProfile } from "../api/geoProfile";
import { isPrefetch } from "@/lib/prefetch";
import { getImageUrl } from "src/utils/utils";

// Cookie Utils
import {
    getBypassLeadFormServerSide,
    getServerCookie,
    getCookieObject,
    updateUserCookie,
} from "src/utils/cookies/server";
import { generateRandomCookieValue } from "src/utils/cookies/shared";

// Constants
import {
    DEFAULT_AUTHENTICATED_STRING,
    LANDING_PAGE_COMPLETE_COOKIE_NAME,
    SESSION_COOKIE_NAME,
    USER_COOKIE_NAME,
} from "src/constants";

// Hooks
import { useSessionData } from "../hooks/useCurrentSession";
import { exportServerTimings } from "@/utils/serverTiming";
import { startServerTimeLog } from "@/logging/logger";
type OffersPageType = ({
    site,
    bypassLeadFormServerSide,
    initialViewportWidth,
    pageData
}: {
    site: Site;
    bypassLeadFormServerSide: boolean;
    initialViewportWidth: number;
    pageData: PageData
}) => React.ReactElement;

const onRenderProfiler = (
    id: string,
    phase: string,
    actualDuration: number
) => {
    console.log(`${id} - ${phase} - ${actualDuration}ms`);
};

const getHeaderAndOfferList = (
    site: Site,
    offerPage: OfferPageTemplateResponse,
    initialViewportWidth: number,
    pageUSPAttributes?: USPAttributesGroup | null
): React.ReactElement => {
    const OfferHeader = (): React.ReactElement | null => {
        if (offerPage.data?.attributes?.offer_header?.data) {
            return (
                <OfferSEOHeader
                    offerPage={offerPage}
                    pageUSPAttributes={pageUSPAttributes ?? undefined}
                    initialViewportWidth={initialViewportWidth}
                />
            );
        } else {
            return (
                <Header
                    site={site}
                    offerPage={offerPage}
                    pageUSPAttributes={pageUSPAttributes ?? undefined}
                />
            );
        }
    };

    const skyscraperEnabledAndExists =
        offerPage?.data?.attributes?.showSiteOfTheMonthSkyscraper;

    const pageContentsClass = skyscraperEnabledAndExists
        ? styles.offerListNoMargin
        : commonStyles.pageContents;


    // It is possible to create unresponsive layouts/presentations in the CMS
    // Adding the offerListNoOverflow class here ensures that the offer list
    // not being created responsive does not break the page
    // In future, we could consider adding a new field in the CMS to set a "zoom" property
    // on the offer list, which I have found to be an easy way to fix offer lists
    // that are not responsive enough to fit in the space provided
    const offerContainerClasses = [
        pageContentsClass,
        styles.offerListNoOverflow,
    ].join(" ");

    // If fullWidthHeader is enabled then we need to render the header outside the page contents div
    // Otherwise, we need to render it inside, so it can follow the same width as the offers
    return site?.attributes?.fullWidthHeader ? (
        <>
            <div className={styles.fullWidthHeader}>
                <OfferHeader />
            </div>
            <div className={styles.pageContainer}>
                <div className={offerContainerClasses}>
                    <OfferList
                        offerPage={offerPage}
                        site={site}
                        initialViewportWidth={initialViewportWidth}
                    />
                </div>
            </div>
        </>
    ) : (
        <div className={styles.pageContainer}>
            <div className={offerContainerClasses}>
                <OfferHeader />
                <OfferList
                    offerPage={offerPage}
                    site={site}
                    initialViewportWidth={initialViewportWidth}
                />
            </div>
        </div>
    );
};

export const OffersPage: OffersPageType = ({
    pageData,
    site,
    initialViewportWidth,
}) => {
    const { offerPage, pageUSPAttributes } = pageData;
    const [submitStarted, setSubmitStarted] = useState(false);
    const { sessionData, status } = useSessionData();

    const backgroundOverride = offerPage?.data?.attributes?.backgroundOverride ? offerPage?.data?.attributes?.backgroundOverride.data?.attributes?.url : null;
    
    useEffect(() => {
        if (status === DEFAULT_AUTHENTICATED_STRING && !submitStarted) {
            setSubmitStarted(true);
            submitFromSocials(site, sessionData, status);
        }
    }, [sessionData, site, status, submitStarted]);

    useEffect(() => {
        if (site.attributes.googleTagManagerContainerId && !isPrefetch()) {
            const leadFormRedirectedEventName = "lead-form-redirected";
            const urlParams = new URLSearchParams(window.location.search);
            const from = urlParams.get("from");
            if (from) {
                // only log the new event if there's not already a lead-form-submitted event in the datalayer
                // this is to prevent the event from being logged multiple times if this component re-renders
                const dataLayer = window.dataLayer;
                const eventAlreadyLogged = dataLayer?.some((event) => {
                    return event.event === leadFormRedirectedEventName;
                });
                if (!eventAlreadyLogged) {
                    // Since we are not on currently on the landing page, we need to work out what the landing page URL
                    // was and pass it into the analytics event
                    const baseUrl = window.location.origin;
                    const landingPage = `${baseUrl}${from}`;
                    // 04/06/2024 - QiH have requested that (at least for now) we use a different event name for this
                    // They are not sure whether having the same event name will make things easier or harder.
                    // If we need to change it to be the same event name, swap out the functions below

                    logNewGoogleAnalyticsEvent(
                        leadFormRedirectedEventName,
                        { provider: "already entered" },
                        landingPage
                    );
                }
            }
        }
    }, [site]);

    if (!offerPage) {
        return <></>;
    }

    const OfferModal = offerPage?.sanitisedOfferModal
        ? dynamic(() => import("@/components/common/Modals/marketing-modal"), {
              ssr: false,
          })
        : null;

    return (
        <Profiler id="OffersPage" onRender={onRenderProfiler}>
            <main className={commonStyles.container} style={
            backgroundOverride ? {'--offer-background-img': `url(${getImageUrl(backgroundOverride)})`} as React.CSSProperties : undefined
        }>
                <Branding site={site} />
                {!!site?.attributes?.offerClaimCounter &&
                    !!offerPage?.offersClaimed?.lifetimeOffersClaimed && (
                        <div className={styles.fullWidthHeader}>
                            <OfferClaimRibbon
                                offersClaimed={
                                    offerPage.offersClaimed
                                        .lifetimeOffersClaimed
                                }
                            />
                        </div>
                    )}
                {getHeaderAndOfferList(
                    site,
                    offerPage,
                    initialViewportWidth,
                    pageUSPAttributes
                )}
                {offerPage.data?.attributes?.sanitisedLowerContentBlockText.trim() !=
                    "" && (
                    <LowerContentBlock
                        lowerContentBlockText={
                            offerPage.data?.attributes
                                ?.sanitisedLowerContentBlockText ?? ""
                        }
                        fromOffersPage={true}
                    />
                )}
                {offerPage?.data?.attributes?.authorBlock?.author?.data
                    ?.attributes ? (
                    <AuthorSection
                        authorBlock={offerPage.data.attributes.authorBlock}
                    />
                ) : null}
                {site?.attributes?.offerClaimCounter &&
                !offerPage?.data?.attributes?.hideOffersClaimedPopup ? (
                    <OfferClaimPopup
                        nOffersClaimed={
                            offerPage?.offersClaimed?.nOffersClaimed
                        }
                        tTimePeriod={offerPage?.offersClaimed?.tTimePeriod}
                    />
                ) : null}
                {OfferModal && offerPage.sanitisedOfferModal && (
                    <OfferModal
                        modalProps={offerPage.sanitisedOfferModal}
                        site={site}
                        offerPage={offerPage}
                    />
                )}
            </main>
        </Profiler>
    );
};

export const getServerSideProps: GetServerSideProps<PageProps> = async (
    context
) => {
    const { endTimeLog } = startServerTimeLog("getServerSideProps", {
        page: "index",
    });

    const visitId = generateRandomCookieValue();
    const sitePromise = getAppProps({
        context,
        sitePopulateQuery: getSitePopulateQuery(),
        visitId,
    });

    const userCookie = getCookieObject<UserCookie>(context, USER_COOKIE_NAME);
    const sessionCookie = getCookieObject<SessionCookie>(
        context,
        SESSION_COOKIE_NAME
    );

    const { site, siteTheme, pageInUse, pageThemePromise } =
        await getPageAndSite<OfferPageTemplateResponse>(
            sitePromise,
            getDefaultOfferPageFromSite,
            getOfferPage,
            validateOfferPage,
            makeOfferPageTheme,
            context.query
        );

    if (pageInUse) {
        pageInUse.offersClaimed = getOfferClaimCounts(context, site);
    }

    // Don't await this promise, let it complete asynchronously in the background
    // It takes too long and we can just let it complete after the page gets rendered to the user
    // as it doesn't affect the rendering.
    visitReport({
        context,
        visitId,
        site,
        offerPage: pageInUse,
    });

    const pageTheme = await getPageThemeAndUpdateCookies<UserCookie>(
        pageThemePromise,
        updateUserCookie,
        context,
        getGlobalConfig(),
        (currentValue) => {
            return {
                completed: isLandingPageCompleted(context, currentValue),
            };
        }
    );

    const landingPageCompleteClientSide = getServerCookie(
        context,
        LANDING_PAGE_COMPLETE_COOKIE_NAME
    );

    const bypassLeadFormServerSide = getBypassLeadFormServerSide(
        userCookie,
        sessionCookie,
        !!landingPageCompleteClientSide,
        pageInUse?.data?.attributes?.locked_landing_page
    );

    const pageUSPAttributes = getPageUSPAttributes(site, null, pageInUse);

    // We do not need to return the full list of offer page templates or campaigns to the frontend
    // these are only needed in order to identify the correct offer page to use
    delete site?.attributes.offer_page_templates;
    delete site?.attributes?.campaigns;

    const ua = context.req.headers["user-agent"];
    const parser = new UAParser(ua);
    const deviceType = parser.getDevice().type;

    let initialViewportWidth = 1920;
    if (deviceType === "mobile") {
        initialViewportWidth = 375;
    } else if (deviceType === "tablet") {
        initialViewportWidth = 768;
    }

    await sanitiseGeoProfile(pageInUse?.data?.attributes?.geo_profile, site);
    context.res.setHeader("Server-Timing", exportServerTimings());

    const pageData: PageData = {
        pageTheme,
        offerPage: pageInUse,
        pageUSPAttributes,
        landingPage: null,
        auj: null,
        page: null,
        userCookie,
    }

    endTimeLog();

    return {
        props: {
            site,
            siteTheme,
            bypassLeadFormServerSide,
            initialViewportWidth,
            pageData,
            page: null,
        },
        notFound: !pageInUse?.data,
    };
};

export default OffersPage;
